import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useExam } from '../../context/ExamContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { useModules } from '../../context/LevelsContext';
import api, { examService, preferencesService } from '../../services/api';
import { userKey } from '../../utils/userStorage';

// Utiliser directement les codes de la BD
const CATEGORY_CODES = {
    'droitAerien': 'droitAerien',
    'meteo': 'meteo',
    'navigation': 'navigation',
    'connaissance': 'connaissance',
    'complet': 'complet'
};

// Niveaux avec modules (excluent PSTAR, IFR qui n'ont pas de modules)
const LEVELS_WITH_MODULES = ['ppl', 'cpl', 'cpl-h', 'ppl-h', 'faaaa', 'saron', 'samra', 'fiften', 'iatra', 'fd-ops', 'fd-met'];

const useLevelOverview = () => {
    const { levelId } = useParams();
    const navigate = useNavigate();
    const { user, refreshNotificationCount, previewMode: isAdminPreview } = useAuth();
    const { theme } = useTheme();
    const { startExam, startReviewFromHistory, startReviewFromCustomHistory, examId: activeExamId, setExamTitle } = useExam();
    const { language } = useLanguage();

    // Helper: derive a {fr, en} title object from an exam (catalog item or history entry).
    // ExamHeader picks the right language at render-time, so a FR↔EN switch updates the label live.
    const titleForExam = useCallback((exam) => {
        if (!exam) return null;
        // exam.title is already a {fr, en} object on catalog items
        if (exam.title && typeof exam.title === 'object') return exam.title;
        if (typeof exam.title === 'string') return { fr: exam.title, en: exam.title };
        // History/custom entries: separate libelleFr/libelleEn fields
        const fr = exam.libelleFr || exam.title_fr || '';
        const en = exam.libelleEn || exam.title_en || fr;
        return (fr || en) ? { fr: fr || en, en: en || fr } : null;
    }, []);

    // State
    const [activeTab, setActiveTab] = useState('exams');
    const [activeSessions, setActiveSessions] = useState({});
    const [showModeModal, setShowModeModal] = useState(null);
    const [activeExams, setActiveExams] = useState([]);
    const [history, setHistory] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('complet');
    const [statsCategory, setStatsCategory] = useState('complet');
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(true);
    const [error, setError] = useState(null);
    const [selectedModule, setSelectedModule] = useState(null);
    const [upgradeModal, setUpgradeModal] = useState({ isOpen: false, exam: null });
    const [newAchievementCodes, setNewAchievementCodes] = useState([]);
    const [lastOpenedExamId, setLastOpenedExamId] = useState(() => {
        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            if (raw) {
                const prefs = JSON.parse(raw);
                return prefs.lastOpenedExam?.[levelId] || null;
            }
        } catch {}
        return null;
    });
    const [lastReviewedHistoryId, setLastReviewedHistoryId] = useState(() => {
        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            if (raw) {
                const prefs = JSON.parse(raw);
                return prefs.lastReviewedHistory?.[levelId] || null;
            }
        } catch {}
        return null;
    });
    const [lastActiveCustomId, setLastActiveCustomId] = useState(() => {
        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            if (raw) {
                const prefs = JSON.parse(raw);
                return prefs.lastActiveCustom?.[levelId] || null;
            }
        } catch {}
        return null;
    });

    const { modules: levelModules } = useModules();
    const currentModule = levelModules.find(m => m.id === levelId) || levelModules[0];
    const hasModules = LEVELS_WITH_MODULES.includes(levelId);

    // Tab & category persistence refs
    const tabRestoredRef = useRef(false);
    const categoryRestoredRef = useRef(false);
    const tabSyncTimeoutRef = useRef(null);
    const categorySyncTimeoutRef = useRef(null);

    // Helper: isLightColor
    const isLightColor = (hexColor) => {
        if (!hexColor) return false;
        const hex = hexColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return brightness > 140;
    };

    // Reset state on module change — restore saved tab + category or defaults
    useEffect(() => {
        setSelectedCategory('complet');
        setStatsCategory('complet');
        setError(null);
        setSelectedModule(null);
        categoryRestoredRef.current = false;

        // Restore last opened exam and last reviewed history for this level
        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            if (raw) {
                const prefs = JSON.parse(raw);
                setLastOpenedExamId(prefs.lastOpenedExam?.[levelId] || null);
                setLastReviewedHistoryId(prefs.lastReviewedHistory?.[levelId] || null);
            } else {
                setLastOpenedExamId(null);
                setLastReviewedHistoryId(null);
            }
        } catch { setLastOpenedExamId(null); setLastReviewedHistoryId(null); }

        let restoredTab = false;

        // Try to restore saved tab and category for this level
        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            if (raw) {
                const prefs = JSON.parse(raw);

                // Restore tab
                const savedTab = prefs.lastTabs?.[levelId];
                if (savedTab) {
                    setActiveTab(savedTab);
                    restoredTab = true;
                }

                // Restore exam category
                const savedCategory = prefs.lastCategories?.[levelId];
                if (savedCategory && savedCategory !== 'complet') {
                    setSelectedCategory(savedCategory);
                    setStatsCategory(savedCategory);
                    categoryRestoredRef.current = true;
                }
            }
        } catch { /* ignore */ }

        if (!restoredTab) {
            setActiveTab('exams');
        }
        tabRestoredRef.current = restoredTab;
    }, [levelId]);

    // Cleanup sync timeouts on unmount
    useEffect(() => {
        return () => {
            if (tabSyncTimeoutRef.current) clearTimeout(tabSyncTimeoutRef.current);
            if (categorySyncTimeoutRef.current) clearTimeout(categorySyncTimeoutRef.current);
        };
    }, []);

    // Load Data
    const loadStats = async () => {
        setLoadingStats(true);
        setError(null);
        try {
            // 1. Fetch Stats
            const statsData = await api.getStats(levelId);
            setStats(statsData);

            // Detect newly generated achievement codes
            if (statsData?.all?.newCodesGenerated > 0) {
                setNewAchievementCodes(statsData.all.achievementCodes || []);
                refreshNotificationCount();
            }

            // 2. Fetch Exams
            const examsData = await api.getExams(levelId);
            setActiveExams(examsData);

            // 3. Fetch History (only when numericId is available to avoid fetching ALL levels)
            if (user?.id && currentModule?.numericId) {
                const historyData = await api.getHistory(user.id, currentModule.numericId);
                setHistory(historyData);
            } else {
                setHistory([]);
            }

            // 4. Fetch Sessions — normalize keys to numeric exam IDs
            const rawSessions = await examService.getAllActiveSessions();
            // Backend resolves composite IDs (e.g. "ppl-complet-123456") to numeric IDs,
            // but as a safety net, re-check and match any remaining composite keys
            // against the loaded exams so activeSessions[exam.id] always works.
            const normalizedSessions = {};
            for (const [key, sessionData] of Object.entries(rawSessions)) {
                if (/^\d+$/.test(key)) {
                    // Already numeric
                    normalizedSessions[key] = sessionData;
                } else {
                    // Composite ID like "ppl-complet-1706789012345" — try to match an exam
                    const parts = key.split('-');
                    if (parts.length >= 2) {
                        const cat = parts[1];
                        const matchCat = (cat === 'full') ? 'complet' : cat;
                        const matched = examsData.find(e =>
                            e.category === matchCat || (matchCat === 'complet' && e.type === 'exam' && e.category === 'complet')
                        );
                        if (matched) {
                            normalizedSessions[String(matched.id)] = sessionData;
                        } else {
                            normalizedSessions[key] = sessionData;
                        }
                    } else {
                        normalizedSessions[key] = sessionData;
                    }
                }
            }
            setActiveSessions(normalizedSessions);
        } catch (error) {
            console.error("Error loading data:", error);
            setError("Impossible de charger les données. Veuillez réessayer.");
        } finally {
            setLoadingStats(false);
        }
    };

    useEffect(() => {
        loadStats();
    }, [levelId, activeExamId, user?.id]); // Added user.id dependency for safety

    // Smart default tab (F1): after stats load, if no saved tab for this level,
    // set tab based on access (only relevant for levels with courses)
    useEffect(() => {
        if (!loadingStats && !tabRestoredRef.current && currentModule?.hasCourses && stats) {
            const hasExamAccess = stats.all?.hasExamAccess ?? true;
            const hasCourseAccess = stats.all?.hasCourseAccess ?? false;

            if (!hasExamAccess && hasCourseAccess) {
                setActiveTab('courses');
            }
        }
    }, [loadingStats, stats, currentModule?.hasCourses]);

    // Persistent tab setter — saves to localStorage + debounced DB sync
    const handleSetActiveTab = useCallback((tab) => {
        setActiveTab(tab);
        tabRestoredRef.current = true;

        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            const prefs = raw ? JSON.parse(raw) : {};
            const lastTabs = { ...prefs.lastTabs, [levelId]: tab };
            prefs.lastTabs = lastTabs;
            localStorage.setItem(userKey('nizus_dashboard_prefs'), JSON.stringify(prefs));

            // Debounced DB sync (1s)
            if (tabSyncTimeoutRef.current) clearTimeout(tabSyncTimeoutRef.current);
            tabSyncTimeoutRef.current = setTimeout(() => {
                preferencesService.save({ lastTabs }).catch(() => {});
            }, 1000);
        } catch { /* ignore */ }
    }, [levelId]);

    // Persistent category setter — saves to localStorage + debounced DB sync
    const handleSetSelectedCategory = useCallback((category) => {
        setSelectedCategory(category);
        setStatsCategory(category);

        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            const prefs = raw ? JSON.parse(raw) : {};
            const lastCategories = { ...prefs.lastCategories, [levelId]: category };
            prefs.lastCategories = lastCategories;
            localStorage.setItem(userKey('nizus_dashboard_prefs'), JSON.stringify(prefs));

            // Debounced DB sync (1s)
            if (categorySyncTimeoutRef.current) clearTimeout(categorySyncTimeoutRef.current);
            categorySyncTimeoutRef.current = setTimeout(() => {
                preferencesService.save({ lastCategories }).catch(() => {});
            }, 1000);
        } catch { /* ignore */ }
    }, [levelId]);

    // Computed Data
    const filteredHistory = (!history || history.length === 0) ? [] :
        (!selectedCategory ? history : history.filter(h => h.category === selectedCategory));

    const keywordsExam = activeExams.find(e => e.category === 'keywords');

    // Filtrage des exams par catégorie + filtre "non terminé"
    const [showIncompleteOnly, setShowIncompleteOnly] = useState(false);

    const regularExams = activeExams.filter(e => {
        // Exclure les keywords
        if (e.category === 'keywords') return false;

        // Filtrer par catégorie exacte (y compris 'complet')
        if (e.category !== selectedCategory) return false;

        // Filtre "Non terminé" : progression < 100%, ou session active entièrement répondue mais non validée
        if (showIncompleteOnly && (e.progression || 0) >= 100) {
            const session = activeSessions[e.id];
            if (!session) return false;
            const sessionAnswers = session.answers || {};
            const realAnswers = Object.values(sessionAnswers).filter(a => a !== 0 && a !== '0' && a !== undefined && a !== null).length;
            if (realAnswers < (e.questionsCount || 0)) return false;
        }

        return true;
    });

    const getStatsDetail = (cat) => {
        if (!stats) return null;
        const baseStats = stats.all || {};
        if (!cat || !stats[cat]) return baseStats;
        return { ...baseStats, ...stats[cat] };
    };

    const statsDetail = getStatsDetail(selectedCategory);

    // Extraire les CATÉGORIES uniques depuis les exams
    const extractCategories = () => {
        if (activeExams.length === 0) return [];
        
        const categorySet = new Set();
        const categoryMap = {};

        activeExams.forEach(exam => {
            if (exam.category !== 'keywords') {
                const cat = exam.category;
                
                if (!categoryMap[cat]) {
                    const labels = {
                        'droitAerien': { fr: 'Droit Aérien', en: 'Air Law' },
                        'meteo': { fr: 'Météorologie', en: 'Meteorology' },
                        'navigation': { fr: 'Navigation', en: 'Navigation' },
                        'connaissance': { fr: 'Connaissance Générale', en: 'General Knowledge' },
                        'complet': { fr: 'Examen Complet', en: 'Full Exam' }
                    };

                    categoryMap[cat] = {
                        id: cat,
                        label: labels[cat] || { fr: cat, en: cat }
                    };
                }
                categorySet.add(cat);
            }
        });

        return Array.from(categorySet).map(cat => categoryMap[cat]);
    };

    const modules = extractCategories(); // Les "modules" = les catégories

    // Passing score options from stats (sourced from DB)
    const passingScoreOpts = {
        passingScore: stats?.all?.minScore ?? 75,
        passingSubScore: stats?.all?.minSubScore ?? 0,
    };

    // Actions
    const handleExamAction = (exam) => {
        // Block access to paid exams - show upgrade modal
        if (exam.requiresUpgrade) {
            setUpgradeModal({ isOpen: true, exam });
            return;
        }

        // Track last opened exam
        setLastOpenedExamId(exam.id);
        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            const prefs = raw ? JSON.parse(raw) : {};
            prefs.lastOpenedExam = { ...(prefs.lastOpenedExam || {}), [levelId]: exam.id };
            localStorage.setItem(userKey('nizus_dashboard_prefs'), JSON.stringify(prefs));
        } catch {}

        const session = activeSessions[exam.id];
        if (session) {
            // Use the session's original examId (may be composite like "ppl-complet-123456")
            // so the backend can find it in examCurrentSessions.es_nroExamen
            const resumeId = session.examId || exam.id;
            setExamTitle(titleForExam(exam));
            startExam(levelId, exam.category || 'full', session.mode, resumeId, { ...passingScoreOpts, annexe: !!exam.hasAnnexe, vuExamen: !!exam.isVuExamen });
        } else {
            setShowModeModal(exam);
        }
    };

    const closeUpgradeModal = () => {
        setUpgradeModal({ isOpen: false, exam: null });
    };

    const startNewExam = (mode) => {
        if (!showModeModal) return;
        const sessionId = showModeModal.id;
        setExamTitle(titleForExam(showModeModal));
        startExam(levelId, showModeModal.category || 'full', mode, sessionId, { ...passingScoreOpts, annexe: !!showModeModal.hasAnnexe, vuExamen: !!showModeModal.isVuExamen });
        setShowModeModal(null);
    };

    // Title for the 3 custom-exam types launched from the "Sur mesure" tab.
    // - error    → ErrorExamSection ("Cible tes erreurs")
    // - revision → RevisionExamSection ("Pas vues depuis N jours")
    // - default  → KeywordExamSection (free-text search)
    const customExamTitleFor = (examType) => {
        if (examType === 'error')    return { fr: 'Cible tes erreurs', en: 'Target your mistakes' };
        if (examType === 'revision') return { fr: 'Révision',           en: 'Revision' };
        return                              { fr: 'Mots-clés',          en: 'Keywords' };
    };

    // Keyword/error/revision exams: start a virtual exam with preloaded questions
    // customExamMeta carries config info for saving to custom history
    const handleStartKeywordExam = (questions, mode, customExamMeta = null) => {
        const kwExamId = `kw-${levelId}-${Date.now()}`;
        setExamTitle(customExamTitleFor(customExamMeta?.examType));
        startExam(levelId, 'keyword-custom', mode, kwExamId, {
            ...passingScoreOpts,
            preloadedQuestions: questions,
            keywordExam: true,
            customExamMeta: customExamMeta ? {
                ...customExamMeta,
                levelId,
                questionIds: questions.map(q => q.id)
            } : null
        });
    };

    const trackLastActiveCustom = (id) => {
        setLastActiveCustomId(id);
        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            const prefs = raw ? JSON.parse(raw) : {};
            prefs.lastActiveCustom = { ...(prefs.lastActiveCustom || {}), [levelId]: id };
            localStorage.setItem(userKey('nizus_dashboard_prefs'), JSON.stringify(prefs));
        } catch {}
    };

    const handleReviewCustomSession = async (customHistoryId) => {
        trackLastActiveCustom(customHistoryId);
        try {
            const fullEntry = await api.getCustomExamHistoryEntry(customHistoryId);
            if (!fullEntry) {
                console.error('Custom history entry not found:', customHistoryId);
                return;
            }
            // Same titles as play mode — the MODE RÉVISION badge already conveys the review context.
            setExamTitle(customExamTitleFor(fullEntry?.examType));
            await startReviewFromCustomHistory(fullEntry, passingScoreOpts);
        } catch (error) {
            console.error('Failed to load custom history entry:', error);
        }
    };

    const handleRestartCustomSession = async (entry) => {
        trackLastActiveCustom(entry.id);
        try {
            const questions = await api.getQuestionsByIds(entry.questionIds);
            if (!questions || questions.length === 0) return;
            handleStartKeywordExam(questions, entry.mode === 'QA' ? 'flash' : 'classic', {
                examType: entry.examType,
                config: entry.config,
                pageIndex: entry.pageIndex || 0,
                dateStart: new Date().toISOString(),
            });
        } catch (error) {
            console.error('Failed to restart custom session:', error);
        }
    };

    const handleReviewSession = async (historyId, isVuExamen) => {
        // Track last reviewed history entry
        setLastReviewedHistoryId(historyId);
        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            const prefs = raw ? JSON.parse(raw) : {};
            prefs.lastReviewedHistory = { ...(prefs.lastReviewedHistory || {}), [levelId]: historyId };
            localStorage.setItem(userKey('nizus_dashboard_prefs'), JSON.stringify(prefs));
        } catch {}

        try {
            const fullEntry = await api.getHistoryEntry(historyId, isVuExamen ? 'vu' : undefined);
            if (!fullEntry) {
                console.error('History entry not found:', historyId);
                return;
            }
            // Special keywords exam (PSTAR): force the localized label to stay consistent
            // with play mode, regardless of how the libellé is stored in DB
            // (legacy rows sometimes have "Mot cles" without accents/dash).
            const isKeywordsExam = fullEntry.category === 'keywords';
            const baseFr = isKeywordsExam
                ? 'Mots-clés'
                : (fullEntry.title?.fr || fullEntry.libelleFr || fullEntry.examName
                    || (isVuExamen ? "Vu à l'examen" : ''));
            const baseEn = isKeywordsExam
                ? 'Keywords'
                : (fullEntry.title?.en || fullEntry.libelleEn || fullEntry.examName
                    || (isVuExamen ? 'Seen on exam' : baseFr));
            setExamTitle({
                fr: baseFr || 'Révision',
                en: baseEn || 'Review',
            });
            await startReviewFromHistory(fullEntry, passingScoreOpts);
        } catch (error) {
            console.error('Failed to load history entry:', error);
        }
    };

    return {
        // State
        levelId,
        theme,
        activeTab,
        setActiveTab: handleSetActiveTab,
        selectedCategory,
        setSelectedCategory: handleSetSelectedCategory,
        statsCategory,
        setStatsCategory,
        showModeModal,
        setShowModeModal,
        loadingStats,
        error,
        hasModules,
        selectedModule,
        setSelectedModule,
        modules,
        upgradeModal,
        closeUpgradeModal,

        // Data
        currentModule,
        // hasCategories: true if minSubScore > 0 (backend-driven instead of static mockData)
        hasCategories: (stats?.all?.minSubScore ?? 0) > 0,
        statsDetail,
        filteredHistory,
        keywordsExam,
        regularExams,
        showIncompleteOnly,
        setShowIncompleteOnly,
        activeExams,
        activeSessions,
        lastOpenedExamId,
        lastReviewedHistoryId,
        lastActiveCustomId,
        stats, // Exposed if needed directly
        isAdminPreview,
        newAchievementCodes,
        dismissNewCodes: () => setNewAchievementCodes([]),

        // Actions
        loadStats,
        handleExamAction,
        startNewExam,
        handleStartKeywordExam,
        handleReviewSession,
        handleReviewCustomSession,
        handleRestartCustomSession,

        // Helpers
        isLightColor,
        categoryRestoredRef,
    };
};

export default useLevelOverview;
